import React from 'react';
import Icon from '../../../components/AppIcon';
import { useLanguage } from '../../../contexts/LanguageContext';

const CaseHistoryTab = ({ history }) => {
  const { t } = useLanguage();

  const eventTypeConfig = {
    status_change: {
      icon: 'RefreshCw',
      color: 'text-primary',
      bgColor: 'bg-primary/10'
    },
    document_upload: {
      icon: 'Upload',
      color: 'text-success',
      bgColor: 'bg-success/10'
    },
    document_approval: {
      icon: 'CheckCircle',
      color: 'text-success',
      bgColor: 'bg-success/10'
    },
    document_rejection: {
      icon: 'XCircle',
      color: 'text-error',
      bgColor: 'bg-error/10'
    },
    payment_received: {
      icon: 'DollarSign',
      color: 'text-success',
      bgColor: 'bg-success/10'
    },
    communication: {
      icon: 'MessageSquare',
      color: 'text-accent',
      bgColor: 'bg-accent/10'
    },
    group_assignment: {
      icon: 'Users',
      color: 'text-primary',
      bgColor: 'bg-primary/10'
    },
    visa_update: {
      icon: 'FileCheck',
      color: 'text-warning',
      bgColor: 'bg-warning/10'
    }
  };

  const formatDateTime = (timestamp) => {
    const date = new Date(timestamp);
    return {
      date: date?.toLocaleDateString('en-US'),
      time: date?.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      })
    };
  };

  return (
    <div className="space-y-6 md:space-y-8">
      <h3 className="text-lg md:text-xl font-semibold text-foreground">
        {t('pilgrimProfile.caseHistory')}
      </h3>
      {history?.length === 0 ? (
        <div className="bg-card rounded-lg border border-border p-8 md:p-12 text-center">
          <Icon name="History" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">
            {t('pilgrimProfile.noHistory')}
          </p>
        </div>
      ) : (
        <div className="bg-card rounded-lg border border-border p-4 md:p-6">
          <h4 className="text-base md:text-lg font-medium text-foreground mb-6 md:mb-8 flex items-center gap-2">
            <Icon name="Clock" size={20} className="text-primary" />
            {t('pilgrimProfile.timeline')}
          </h4>

          <div className="relative">
            <div className="absolute left-6 md:left-8 top-0 bottom-0 w-0.5 bg-border" />

            <div className="space-y-6 md:space-y-8">
              {history?.map((event, index) => {
                const config = eventTypeConfig?.[event?.type] || eventTypeConfig?.status_change;
                const dateTime = formatDateTime(event?.timestamp);

                return (
                  <div key={event?.id} className="relative pl-16 md:pl-20">
                    <div className={`absolute left-0 w-12 h-12 md:w-16 md:h-16 rounded-full ${config?.bgColor} flex items-center justify-center border-4 border-background`}>
                      <Icon name={config?.icon} size={20} className={config?.color} />
                    </div>
                    <div className="bg-muted/50 rounded-lg p-4 md:p-6">
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 mb-3">
                        <h5 className="text-sm md:text-base font-medium text-foreground">
                          {event?.title}
                        </h5>
                        <div className="flex items-center gap-2 text-xs md:text-sm text-muted-foreground">
                          <Icon name="Calendar" size={14} />
                          <span>{dateTime?.date}</span>
                          <Icon name="Clock" size={14} />
                          <span>{dateTime?.time}</span>
                        </div>
                      </div>

                      <p className="text-sm text-muted-foreground mb-3">
                        {event?.description}
                      </p>

                      {event?.details && (
                        <div className="bg-background rounded-md p-3 space-y-2">
                          {Object.entries(event?.details)?.map(([key, value]) => (
                            <div key={key} className="flex justify-between text-xs md:text-sm">
                              <span className="text-muted-foreground">{key}:</span>
                              <span className="font-medium text-foreground">{value}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border">
                        <Icon name="User" size={14} className="text-muted-foreground" />
                        <span className="caption text-muted-foreground">
                          {event?.performedBy}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CaseHistoryTab;